/**
 * useFiasAIChat - Core reusable hook for arche AI chat.
 *
 * Handles message state, streaming, proposal management, and auto-apply.
 * Generalized from the make-a-request useFiasAI pattern.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { api, WEBSOCKET_ENDPOINT } from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { useStreaming } from '@/lib/hooks/useStreaming';
import type { ArcheAICapabilities, ArcheProposal } from '../types';

const MAX_CONVERSATION_MESSAGES = 20;

export interface FiasAIChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  proposals?: ArcheProposal[];
  proposalStatus?: Record<string, 'applied' | 'rejected'>;
  modelInfo?: {
    provider: string;
    modelName: string;
    displayName: string;
    tier: string;
  } | null;
}

interface UseFiasAIChatOptions {
  capabilities: ArcheAICapabilities;
}

interface UseFiasAIChatReturn {
  messages: FiasAIChatMessage[];
  isStreaming: boolean;
  currentModel: {
    provider: string;
    modelName: string;
    displayName: string;
    tier: string;
  } | null;
  submit: (message: string) => Promise<void>;
  stop: () => void;
  reset: () => void;
  applyProposal: (proposalId: string) => void;
  rejectProposal: (proposalId: string) => void;
}

export function useFiasAIChat({ capabilities }: UseFiasAIChatOptions): UseFiasAIChatReturn {
  const [messages, setMessages] = useState<FiasAIChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentModel, setCurrentModel] = useState<UseFiasAIChatReturn['currentModel']>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [currentAssistantMessageId, setCurrentAssistantMessageId] = useState<string | null>(null);

  const authToken = authApi.getIdToken();

  // Use refs for values accessed in the streaming callback to avoid
  // stale closures and prevent useStreaming from re-registering all
  // WebSocket callbacks every time these change.
  const capabilitiesRef = useRef(capabilities);
  capabilitiesRef.current = capabilities;
  const assistantMessageIdRef = useRef(currentAssistantMessageId);
  assistantMessageIdRef.current = currentAssistantMessageId;

  // Handle arche proposals from WebSocket — stable callback via refs
  const handleArcheProposal = useCallback(
    (proposal: {
      proposalId: string;
      actions: Array<{ actionId: string; params: Record<string, unknown>; description: string }>;
    }) => {
      const caps = capabilitiesRef.current;
      const msgId = assistantMessageIdRef.current;

      // Auto-apply all actions immediately
      for (const action of proposal.actions) {
        caps.applyAction(action.actionId, action.params);
      }

      // Attach proposal to assistant message (marked as applied)
      if (msgId) {
        const appliedProposal: ArcheProposal = {
          proposalId: proposal.proposalId,
          actions: proposal.actions,
        };

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== msgId) return msg;
            return {
              ...msg,
              proposals: [...(msg.proposals || []), appliedProposal],
              proposalStatus: {
                ...msg.proposalStatus,
                [proposal.proposalId]: 'applied' as const,
              },
            };
          }),
        );
      }
    },
    [], // Stable — reads from refs
  );

  // WebSocket streaming hook
  const {
    streamingText,
    isComplete: streamingComplete,
    error: streamingError,
    reset: resetStreaming,
  } = useStreaming({
    requestId: currentRequestId,
    authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    // Pre-warm the WS on auth — see useStreaming.ts.
    enabled: !!authToken,
    onArcheProposal: handleArcheProposal,
  });

  // Combine streaming text
  const combinedStreamingText = useMemo(() => {
    return Object.values(streamingText).join('');
  }, [streamingText]);

  // Update assistant message with streaming text
  useEffect(() => {
    if (currentAssistantMessageId && combinedStreamingText) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId ? { ...msg, content: combinedStreamingText } : msg,
        ),
      );
    }
  }, [currentAssistantMessageId, combinedStreamingText]);

  // Handle streaming completion
  useEffect(() => {
    if (streamingComplete && currentAssistantMessageId) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId ? { ...msg, isStreaming: false } : msg,
        ),
      );
      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      resetStreaming();
    }
  }, [streamingComplete, currentAssistantMessageId, resetStreaming]);

  // Handle streaming errors
  useEffect(() => {
    if (streamingError && currentAssistantMessageId) {
      const isAuthError =
        streamingError.message.toLowerCase().includes('unauthorized') ||
        streamingError.code === 'UNAUTHORIZED';

      const errorMessage = isAuthError
        ? 'Your session has expired. Please log out and log back in to continue.'
        : `Error: ${streamingError.message}`;

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId
            ? { ...msg, content: errorMessage, isStreaming: false }
            : msg,
        ),
      );
      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      resetStreaming();
    }
  }, [streamingError, currentAssistantMessageId, resetStreaming]);

  const submit = useCallback(
    async (message: string) => {
      const trimmed = message.trim();
      if (!trimmed || isStreaming) return;

      const userMessageId = `user-${Date.now()}`;
      const assistantMessageId = `assistant-${Date.now()}`;

      const userMessage: FiasAIChatMessage = {
        id: userMessageId,
        role: 'user',
        content: trimmed,
      };

      const assistantMessage: FiasAIChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        isStreaming: true,
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setIsStreaming(true);

      // Build conversation history
      const allMessages = [...messages, userMessage].filter((m) => m.content.trim().length > 0);
      const truncated = allMessages.slice(-MAX_CONVERSATION_MESSAGES);
      const conversationHistory = truncated
        .filter((m) => m.role === 'user' || (m.role === 'assistant' && !m.isStreaming))
        .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));

      // Build arche context from capabilities
      const caps = capabilitiesRef.current;
      const archeContext = {
        archeId: caps.archeId,
        state: caps.getStateSnapshot(),
        actions: caps.actions,
      };

      try {
        const result = await api.fiasAIChat({
          message: trimmed,
          conversationId: conversationId || undefined,
          mode: 'ai-only',
          routingPreference: 'auto',
          conversationHistory,
          archeContext,
        });

        setConversationId(result.conversationId);
        setCurrentModel(result.selectedModel);
        setCurrentRequestId(result.requestId);
        setCurrentAssistantMessageId(assistantMessageId);

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId ? { ...msg, modelInfo: result.selectedModel } : msg,
          ),
        );
      } catch (err) {
        console.error('Fias AI arche chat failed:', err);

        const errorStr = String(err).toLowerCase();
        const isAuthError = errorStr.includes('unauthorized') || errorStr.includes('token');
        const errorMessage = isAuthError
          ? 'Your session has expired. Please log out and log back in to continue.'
          : 'Sorry, something went wrong. Please try again.';

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? { ...msg, content: errorMessage, isStreaming: false }
              : msg,
          ),
        );
        setIsStreaming(false);
      }
    },
    [messages, isStreaming, conversationId],
  );

  const stop = useCallback(() => {
    if (!isStreaming || !currentAssistantMessageId) return;
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === currentAssistantMessageId ? { ...msg, isStreaming: false } : msg,
      ),
    );
    setIsStreaming(false);
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    resetStreaming();
  }, [isStreaming, currentAssistantMessageId, resetStreaming]);

  const reset = useCallback(() => {
    setMessages([]);
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    setConversationId(null);
    setCurrentModel(null);
    setIsStreaming(false);
    resetStreaming();
  }, [resetStreaming]);

  const applyProposal = useCallback((proposalId: string) => {
    const caps = capabilitiesRef.current;
    setMessages((prev) =>
      prev.map((msg) => {
        const proposal = msg.proposals?.find((p) => p.proposalId === proposalId);
        if (!proposal) return msg;

        for (const action of proposal.actions) {
          caps.applyAction(action.actionId, action.params);
        }

        return {
          ...msg,
          proposalStatus: {
            ...msg.proposalStatus,
            [proposalId]: 'applied' as const,
          },
        };
      }),
    );
  }, []);

  const rejectProposal = useCallback((proposalId: string) => {
    setMessages((prev) =>
      prev.map((msg) => {
        if (!msg.proposals?.find((p) => p.proposalId === proposalId)) return msg;
        return {
          ...msg,
          proposalStatus: {
            ...msg.proposalStatus,
            [proposalId]: 'rejected' as const,
          },
        };
      }),
    );
  }, []);

  return {
    messages,
    isStreaming,
    currentModel,
    submit,
    stop,
    reset,
    applyProposal,
    rejectProposal,
  };
}
